import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import {
  MdCheckCircleOutline,
  MdOutlineTaskAlt,
  MdOutlineWhatsapp,
  MdUndo,
  MdOutlineEventBusy,
  MdOutlineToday,
  MdOutlineEventAvailable,
  MdOutlineDoneAll,
  MdOutlineOpenInNew,
} from "react-icons/md";
import axios from "../config/axiosInstance";
import { CRM, STAFF } from "../config/apiConfig";
import { useApplication } from "../contexts/ApplicationContext";
import {
  Badge,
  Card,
  EmptyState,
  ErrorBlock,
} from "../components/crm/Primitives";
import {
  SkeletonKpiStrip,
  SkeletonWorkList,
  SkeletonChart,
} from "../components/crm/Skeletons";
import WorkloadChart from "../components/crm/WorkloadChart";
import AccountDrawer from "../components/crm/AccountDrawer";
import {
  formatDate,
  formatNumber,
  formatRelativeDays,
  initialsOf,
  PRIORITY_LABELS,
  whatsappLink,
} from "../components/crm/crmHelpers";
import ClientLink from "../components/crm/ClientLink";

/*
  Caixa de tarefas.

  A versão anterior era três pilhas de cartões e um seletor. Faltava o que
  transforma uma lista de pendências em planejamento:

  - **Quanto trabalho existe, e quanto já foi feito.** Uma tela que só mostra
    pendência dá a impressão de que nada anda. O número de concluídas na
    semana é o contrapeso.
  - **Quando sobra tempo.** "12 tarefas futuras" não ajuda a decidir se dá
    para assumir mais uma; o gráfico de carga por dia, sim.
  - **Trabalhar sem sair da lista.** Clicar numa tarefa abre o painel da
    conta — antes era preciso navegar até a ficha e perder o lugar.
*/

const PRIORITY_TONE = { HIGH: "danger", NORMAL: "info", LOW: "neutral" };

const Kpi = ({ icon: Icon, label, value, hint, tone }) => (
  <div className="flex flex-1 items-start gap-3 border-slate-200 px-5 py-4 sm:border-r sm:last:border-r-0">
    <span
      className={`shrink-0 rounded-xl p-2 ${
        tone === "danger"
          ? "bg-rose-50 text-rose-600"
          : tone === "warning"
          ? "bg-amber-50 text-amber-600"
          : tone === "success"
          ? "bg-emerald-50 text-emerald-600"
          : "bg-slate-100 text-slate-500"
      }`}
    >
      <Icon size={18} />
    </span>
    <span className="min-w-0">
      <span className="block text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">
        {label}
      </span>
      <span className="block text-xl font-bold leading-tight text-slate-900">
        {value}
      </span>
      {hint ? (
        <span className="block text-[11px] text-slate-500">{hint}</span>
      ) : null}
    </span>
  </div>
);

const TaskRow = ({ task, onToggle, onOpen, isBusy, isActive }) => {
  const link = whatsappLink(task.company?.phoneNumber);
  const isDone = task.status !== "OPEN";

  return (
    <div
      className={`flex items-center gap-3 border-b border-slate-100 px-4 py-2.5 transition last:border-0 ${
        isActive ? "bg-pink-50/60" : "hover:bg-slate-50/80"
      }`}
    >
      <button
        type="button"
        onClick={() => onToggle(task)}
        disabled={isBusy}
        title={isDone ? "Reabrir" : "Marcar como feita"}
        className={`shrink-0 transition disabled:opacity-40 ${
          isDone
            ? "text-emerald-500 hover:text-slate-400"
            : "text-slate-300 hover:text-emerald-500"
        }`}
      >
        {isDone ? <MdUndo size={19} /> : <MdCheckCircleOutline size={21} />}
      </button>

      <button
        type="button"
        onClick={() => onOpen(task.companyId)}
        className="flex min-w-0 flex-1 items-center gap-3 text-left"
      >
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-pink-50 text-[10px] font-bold text-pink-700">
          {initialsOf(task.company?.name || "?")}
        </span>

        <span className="min-w-0 flex-1">
          <span
            className={`block truncate text-sm font-semibold ${
              isDone ? "text-slate-400 line-through" : "text-slate-800"
            }`}
          >
            {task.title}
          </span>
          <span className="block truncate text-[11px] text-slate-500">
            {task.company?.name || `Cliente #${task.companyId}`}
            {task.notes ? ` · ${task.notes}` : ""}
          </span>
        </span>
      </button>

      <span className="hidden shrink-0 text-right sm:block">
        <span
          className={`block text-xs font-semibold ${
            task.isOverdue
              ? "text-rose-600"
              : task.isToday
              ? "text-amber-600"
              : "text-slate-600"
          }`}
        >
          {task.dueDate ? formatRelativeDays(task.dueDate) : "sem data"}
        </span>
        {task.dueDate ? (
          <span className="block text-[10px] text-slate-400">
            {formatDate(task.dueDate)}
          </span>
        ) : null}
      </span>

      <span className="flex shrink-0 items-center gap-1">
        {task.priority !== "NORMAL" ? (
          <Badge tone={PRIORITY_TONE[task.priority]}>
            {PRIORITY_LABELS[task.priority]}
          </Badge>
        ) : null}
        {link ? (
          <a
            href={link}
            target="_blank"
            rel="noreferrer"
            title="Abrir WhatsApp"
            className="rounded-lg p-1.5 text-slate-300 transition hover:bg-emerald-50 hover:text-emerald-600"
          >
            <MdOutlineWhatsapp size={17} />
          </a>
        ) : null}
        <ClientLink
          hideWhenBlocked
          to={`/clientes/${task.companyId}`}
          title="Abrir ficha completa"
          className="rounded-lg p-1.5 text-slate-300 transition hover:bg-pink-50 hover:text-pink-600"
        >
          <MdOutlineOpenInNew size={15} />
        </ClientLink>
      </span>
    </div>
  );
};

const Grupo = ({ titulo, tone, tarefas, children, ...rest }) => {
  if (!tarefas.length) return null;

  return (
    <Card className="overflow-hidden">
      <div className="flex items-center gap-2 border-b border-slate-100 bg-slate-50/80 px-4 py-2">
        <Badge tone={tone}>{titulo}</Badge>
        <span className="text-xs text-slate-500">
          {formatNumber(tarefas.length)}
        </span>
        {children}
      </div>
      {tarefas.map((task) => (
        <TaskRow key={task.id} task={task} {...rest} />
      ))}
    </Card>
  );
};

const TasksPage = () => {
  const queryClient = useQueryClient();
  const { isAdminOrOperator, user } = useApplication();
  const [staffUserId, setStaffUserId] = useState("");
  const [showDone, setShowDone] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [openId, setOpenId] = useState(null);

  const { data: staffOptions } = useQuery(
    ["staff", "options"],
    async () => {
      const { data } = await axios.get(STAFF.OPTIONS);
      return data.data;
    },
    { enabled: isAdminOrOperator }
  );

  const tasksQuery = useQuery(
    ["crm", "tasks", staffUserId, showDone],
    async () => {
      const params = new URLSearchParams();
      if (staffUserId) params.set("staffUserId", staffUserId);
      if (showDone) params.set("includeDone", "true");

      const { data } = await axios.get(`${CRM.TASKS}?${params.toString()}`);
      return data.data;
    },
    { keepPreviousData: true }
  );

  const toggleTask = useMutation(
    (task) =>
      axios.put(`${CRM.TASK}/${task.id}`, {
        status: task.status === "OPEN" ? "DONE" : "OPEN",
      }),
    {
      onMutate: (task) => setBusyId(task.id),
      onSettled: () => {
        setBusyId(null);
        queryClient.invalidateQueries(["crm", "tasks"]);
        queryClient.invalidateQueries(["crm", "queues"]);
      },
    }
  );

  const tasks = tasksQuery.data;
  const resumo = tasks?.summary;

  // Ordem de navegação do painel: a mesma da tela, de cima para baixo.
  const sequencia = useMemo(() => {
    if (!tasks) return [];
    return [...tasks.overdue, ...tasks.today, ...tasks.upcoming].map(
      (task) => task.companyId
    );
  }, [tasks]);

  const openIndex = sequencia.indexOf(openId);
  const abertas =
    (tasks?.overdue.length || 0) +
    (tasks?.today.length || 0) +
    (tasks?.upcoming.length || 0);

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Tarefas</h1>
          <p className="text-sm text-slate-500">
            {isAdminOrOperator && staffUserId
              ? "Caixa de outra pessoa da equipe"
              : `Seus compromissos${user?.name ? `, ${user.name.split(" ")[0]}` : ""}`}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {isAdminOrOperator ? (
            <select
              value={staffUserId}
              onChange={(event) => setStaffUserId(event.target.value)}
              className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm outline-none focus:border-pink-300"
            >
              <option value="">Minhas tarefas</option>
              {(staffOptions || []).map((option) => (
                <option key={option.id} value={option.id}>
                  {option.name}
                </option>
              ))}
            </select>
          ) : null}

          <label className="inline-flex cursor-pointer items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm">
            <input
              type="checkbox"
              checked={showDone}
              onChange={(event) => setShowDone(event.target.checked)}
              className="h-3.5 w-3.5 rounded accent-pink-600"
            />
            mostrar concluídas
          </label>
        </div>
      </header>

      {tasksQuery.isLoading ? (
        <>
          <SkeletonKpiStrip count={4} />
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
            <div className="space-y-4 xl:col-span-2">
              <SkeletonWorkList rows={5} />
            </div>
            <SkeletonChart height={140} bars={14} />
          </div>
        </>
      ) : tasksQuery.error ? (
        <ErrorBlock
          message="Não foi possível carregar as tarefas."
          onRetry={tasksQuery.refetch}
        />
      ) : (
        <>
          <Card className="overflow-hidden">
            <div className="flex flex-col divide-y divide-slate-100 sm:flex-row sm:divide-y-0">
              <Kpi
                icon={MdOutlineEventBusy}
                label="Atrasadas"
                value={formatNumber(resumo?.overdue || 0)}
                hint={resumo?.overdue ? "resolva primeiro" : "nada vencido"}
                tone={resumo?.overdue ? "danger" : "success"}
              />
              <Kpi
                icon={MdOutlineToday}
                label="Para hoje"
                value={formatNumber(resumo?.dueToday || 0)}
                hint="vencem no dia"
                tone={resumo?.dueToday ? "warning" : "neutral"}
              />
              <Kpi
                icon={MdOutlineEventAvailable}
                label="Abertas"
                value={formatNumber(resumo?.open || 0)}
                hint={
                  resumo?.withoutDueDate
                    ? `${formatNumber(resumo.withoutDueDate)} sem data`
                    : "todas com data"
                }
                tone="neutral"
              />
              <Kpi
                icon={MdOutlineDoneAll}
                label="Concluídas"
                value={formatNumber(resumo?.completedLast7Days || 0)}
                hint="nos últimos 7 dias"
                tone="success"
              />
            </div>
          </Card>

          <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
            <div className="space-y-4 xl:col-span-2">
              {abertas === 0 && !(showDone && tasks.done.length) ? (
                <Card>
                  <EmptyState
                    icon={MdOutlineTaskAlt}
                    title="Nenhuma tarefa aberta"
                    description="Tarefas nascem na ficha de um cliente ou no painel lateral. Comece por uma fila de trabalho na tela Hoje."
                  />
                </Card>
              ) : (
                <>
                  <Grupo
                    titulo="Atrasadas"
                    tone="danger"
                    tarefas={tasks.overdue}
                    onToggle={toggleTask.mutate}
                    onOpen={setOpenId}
                    isBusy={false}
                    busyId={busyId}
                    isActive={false}
                  />
                  <Grupo
                    titulo="Para hoje"
                    tone="warning"
                    tarefas={tasks.today}
                    onToggle={toggleTask.mutate}
                    onOpen={setOpenId}
                  />
                  <Grupo
                    titulo="Próximas"
                    tone="info"
                    tarefas={tasks.upcoming}
                    onToggle={toggleTask.mutate}
                    onOpen={setOpenId}
                  />
                  {showDone ? (
                    <Grupo
                      titulo="Concluídas"
                      tone="success"
                      tarefas={tasks.done}
                      onToggle={toggleTask.mutate}
                      onOpen={setOpenId}
                    />
                  ) : null}
                </>
              )}
            </div>

            <div className="space-y-4">
              <Card className="overflow-hidden">
                <div className="border-b border-slate-100 px-4 py-3">
                  <h2 className="text-sm font-bold text-slate-900">
                    Carga dos próximos 14 dias
                  </h2>
                  <p className="text-[11px] text-slate-500">
                    Onde há folga para assumir mais um compromisso
                  </p>
                </div>
                <div className="p-2">
                  <WorkloadChart workload={tasks.workload} />
                </div>
              </Card>

              {resumo?.withoutDueDate ? (
                /*
                  Tarefa sem data não aparece no gráfico de carga nem nos
                  grupos por vencimento — vira trabalho invisível. O aviso
                  existe para que ela não se perca.
                */
                <Card className="p-4">
                  <p className="text-xs font-semibold text-amber-800">
                    {formatNumber(resumo.withoutDueDate)} tarefa(s) sem data
                  </p>
                  <p className="mt-0.5 text-[11px] text-slate-500">
                    Elas ficam no fim de "Próximas" e não entram na carga por
                    dia. Definir um vencimento é o que faz uma tarefa voltar
                    sozinha para a sua frente.
                  </p>
                </Card>
              ) : null}
            </div>
          </div>
        </>
      )}

      {openId ? (
        <AccountDrawer
          companyId={openId}
          onClose={() => setOpenId(null)}
          position={openIndex >= 0 ? openIndex + 1 : null}
          total={sequencia.length}
          onPrev={
            openIndex > 0 ? () => setOpenId(sequencia[openIndex - 1]) : undefined
          }
          onNext={
            openIndex >= 0 && openIndex < sequencia.length - 1
              ? () => setOpenId(sequencia[openIndex + 1])
              : undefined
          }
        />
      ) : null}
    </div>
  );
};

export default TasksPage;
